import {
  Banner,
  Button,
  Form,
  Image,
  InlineStack,
  Link,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Text,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import type { SsoConfiguration, SsoIdentityProvider } from '@/services/oauth';
import { useLoginFlow } from '../login-flow';
import { GoogleAuthButton } from '../components/google-auth-button';
import oktaLogo from '@/assets/images/login-okta-logo.svg';
import azureLogo from '@/assets/images/login-azure-logo.svg';

const SSO_BUTTON_LABEL: Record<Exclude<SsoIdentityProvider, 'google_oidc'>, string> = {
  okta_oidc: 'Sign in with Okta',
  azure_oidc: 'Sign in with Azure Active Directory',
  generic_oidc: 'Sign in with OpenID SSO',
  generic_saml: 'Sign in with SAML',
};

const SSO_BUTTON_ICON: Partial<Record<SsoIdentityProvider, string>> = {
  okta_oidc: oktaLogo,
  azure_oidc: azureLogo,
};

function hasGoogleSsoDomainMismatch(
  config: SsoConfiguration,
  email: string,
  userExternalIdentityEmail: string | null,
): boolean {
  // A returning SSO user (has an external identity) is already verified.
  if (userExternalIdentityEmail != null && !isEmptyString(userExternalIdentityEmail)) {
    return false;
  }
  const hostedDomain = config.oidc_google_hosted_domain;
  if (hostedDomain == null || isEmptyString(hostedDomain)) {
    return false;
  }
  // Domains are case-insensitive; compare lowercased so e.g. `@Acme.com`
  // matches a configured `acme.com`.
  return (email.split('@')[1] ?? '').toLowerCase() !== hostedDomain.toLowerCase();
}

export function SsoStep() {
  const { email, ssoConfiguration, userExternalIdentityEmail, isSubmitting, submitSso, resetToEmailStep } =
    useLoginFlow();

  if (ssoConfiguration == null) {
    return null;
  }

  const provider = ssoConfiguration.identity_provider;
  const isGoogle = provider === 'google_oidc';
  const domainMismatch = isGoogle && hasGoogleSsoDomainMismatch(ssoConfiguration, email, userExternalIdentityEmail);

  return (
    <>
      <PanelHeader>
        <PanelTitle level="1" data-test-login-signup-title>
          Welcome back,
          <br />
          <Text data-test-login-form-email-title>{email}</Text>!
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form
          id="login-sso-form"
          onSubmit={(event) => {
            event.preventDefault();
            return submitSso();
          }}
        >
          <Paragraph mb="600">
            Not this account?{' '}
            <Link isUnderlined onClick={resetToEmailStep} data-test-login-form-use-different-email>
              Use a different email
            </Link>
          </Paragraph>

          {domainMismatch ? (
            <Banner variant="error" mb="300" data-test-login-form-google-sso-email-domain-mismatch-banner>
              This account has SSO enabled and requires all team members to log in with an [@
              {ssoConfiguration.oidc_google_hosted_domain}] email. An admin on your account can verify or update your
              email in Customer.io.
            </Banner>
          ) : null}

          {isGoogle ? (
            <GoogleAuthButton
              type="login"
              isDisabled={domainMismatch}
              isLoading={isSubmitting}
              onClick={() => void submitSso()}
              data-testid="login-sso-submit"
            />
          ) : (
            <Button
              type="submit"
              icon="chevron-right"
              iconPosition="trailing"
              w="full"
              isLoading={isSubmitting}
              data-testid="login-sso-submit"
            >
              <InlineStack alignBlock="center" alignInline="space-between" gap="100">
                {SSO_BUTTON_ICON[provider] != null ? (
                  <Image src={SSO_BUTTON_ICON[provider]} alt="" size="icon-v2-md" />
                ) : null}
                <Text>{SSO_BUTTON_LABEL[provider]}</Text>
              </InlineStack>
            </Button>
          )}
        </Form>
      </PanelSection>
    </>
  );
}
